import { HistoryModal } from "@/components/history/HistoryModal";
import AntIcon from "@/components/icons/AntIcon";
import { formatLicensePlate, formatLocaleDate, formatLocaleNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Button, Descriptions, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Vehicle } from "../../../../../types";
import { parseVehicleModelName } from "../../../../../utils";

type Props = {
  vehicle?: Vehicle;
  licensePlate?: string;
  vin?: string;
  registrationCertificateNumber?: string;
};

export const VehicleData = ({ vehicle, licensePlate, vin, registrationCertificateNumber }: Props) => {
  const { t } = useTranslation();

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const licensePlateItem = {
    label: t("contract.attrs.insurances.licensePlate"),
    children: licensePlate ? formatLicensePlate(licensePlate) : undefined,
  };

  const vinItem = {
    label: t("contract.attrs.insurances.vehicle.vin"),
    children: vin ?? vehicle?.vin,
  };

  const items = !vehicle
    ? [licensePlateItem, vinItem]
    : [
        {
          label: t("contract.attrs.insurances.vehicle.modelId"),
          children: parseVehicleModelName({
            customModel: vehicle.customModel,
            customBrand: vehicle.customBrand,
            modelName: vehicle.model.name,
            brandName: vehicle.model.brand.name,
          }),
        },
        {
          label: t("contract.enums.vehicleCategory._label"),
          children: t("contract.enums.vehicleCategory." + vehicle.category),
        },
        licensePlateItem,
        vinItem,
        {
          label: t("contract.attrs.insurances.vehicle.firstRegistrationDate"),
          children: formatLocaleDate(vehicle.firstRegistrationDate),
        },
        {
          label: t("contract.attrs.insurances.insuranceData.registrationCertificateNumber"),
          children: registrationCertificateNumber,
        },
        {
          label: t("contract.attrs.insurances.vehicle.engineDisplacement"),
          children: isDefined(vehicle.engineDisplacement) && (
            <>
              {formatLocaleNumber(vehicle.engineDisplacement)} cm<sup>3</sup>
            </>
          ),
        },
        {
          label: t("contract.attrs.insurances.vehicle.enginePower"),
          children: isDefined(vehicle.enginePower) && (
            <>
              {formatLocaleNumber(vehicle.enginePower)} {t("common.sign.enginePower")}
            </>
          ),
        },
        {
          label: t("contract.attrs.insurances.vehicle.electricEnginePower"),
          children: isDefined(vehicle.electricEnginePower) && (
            <>
              {formatLocaleNumber(vehicle.electricEnginePower)} {t("common.sign.enginePower")}
            </>
          ),
        },
        {
          label: t("contract.enums.fuelType._label"),
          children: vehicle.fuelType ? t("contract.enums.fuelType." + vehicle.fuelType) : undefined,
        },
        {
          label: t("contract.enums.transmission._label"),
          children: vehicle.transmission ? t("contract.enums.transmission." + vehicle.transmission) : undefined,
        },
        {
          label: t("contract.enums.bodywork._label"),
          children: vehicle.bodywork ? t("contract.enums.bodywork." + vehicle.bodywork) : undefined,
        },
        {
          label: t("contract.attrs.insurances.vehicle.totalWeight"),
          children: `${formatLocaleNumber(vehicle.totalWeight)} ${t("common.sign.kg")}`,
        },
        {
          label: t("contract.attrs.insurances.vehicle.seatsNumber"),
          children: vehicle.seatsNumber,
        },
        {
          label: t("contract.attrs.insurances.vehicle.doorsNumber"),
          children: vehicle.doorsNumber,
        },
        {
          label: t("contract.attrs.insurances.vehicle.colorId"),
          children: vehicle.color.value,
        },
      ];

  return (
    <>
      <Descriptions
        title={
          <>
            <span style={{ marginRight: 4 }}>{t("contract.attrs.insurances.vehicle._label")}</span>

            {vehicle && (
              <Tooltip title={t("history.title")}>
                <span>
                  <Button
                    size="small"
                    type="text"
                    icon={<AntIcon type="history" />}
                    onClick={() => setIsHistoryModalOpen(true)}
                  />
                </span>
              </Tooltip>
            )}
          </>
        }
        bordered
        size="small"
        column={2}
        styles={{ header: { marginBottom: 8 } }}
        items={items}
      />

      {vehicle && (
        <HistoryModal
          item={vehicle}
          type="vehicle"
          isOpen={isHistoryModalOpen}
          translationRootPath="contract.attrs.insurances.vehicle"
          onCancel={() => setIsHistoryModalOpen(false)}
        />
      )}
    </>
  );
};
